import type { SpinWheelTriggerConfig } from './types';

export interface TriggerControllerOptions {
  config: SpinWheelTriggerConfig;
  storageKey: string;
  preview: boolean;
  onOpen: () => void;
  onReady: () => void;
}

export class TriggerController {
  private delayTimer?: number;
  private opened = false;
  private removeListeners: Array<() => void> = [];

  start(options: TriggerControllerOptions): void {
    this.stop();
    this.opened = false;
    const recentlyOpened =
      !options.preview &&
      this.hasRecentlyOpened(options.storageKey, options.config.showOncePerDays);

    const mode = options.config.mode;
    if (mode === 'manual') {
      options.onReady();
      return;
    }

    if (mode === 'delay' || mode === 'delay-and-exit') {
      this.delayTimer = window.setTimeout(
        () => options.onReady(),
        Math.max(0, options.config.delayMs),
      );
    }

    if (mode === 'exit-intent') {
      options.onReady();
    }

    if (
      !recentlyOpened &&
      (mode === 'exit-intent' || mode === 'delay-and-exit') &&
      options.config.exitIntentEnabled
    ) {
      this.bindExitIntent(options);
    }
  }

  stop(): void {
    if (this.delayTimer) {
      window.clearTimeout(this.delayTimer);
      this.delayTimer = undefined;
    }

    this.removeListeners.forEach((remove) => remove());
    this.removeListeners = [];
  }

  markOpened(storageKey: string): void {
    try {
      window.localStorage.setItem(storageKey, String(Date.now()));
    } catch {
      // Storage can be blocked in privacy modes. The widget should still work.
    }
  }

  private open(options: TriggerControllerOptions): void {
    if (this.opened) return;
    this.opened = true;
    this.markOpened(options.storageKey);
    options.onReady();
    options.onOpen();
    this.stop();
  }

  private bindExitIntent(options: TriggerControllerOptions): void {
    let maxScrollPercent = 0;

    const onPointerLeave = (event: MouseEvent) => {
      if (event.clientY <= 0) this.open(options);
    };

    const onScroll = () => {
      const documentHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight,
        window.innerHeight,
      );
      const scrollable = documentHeight - window.innerHeight;
      if (scrollable <= 0) return;

      maxScrollPercent = Math.max(maxScrollPercent, (window.scrollY / scrollable) * 100);
      if (maxScrollPercent >= options.config.mobileScrollPercent) {
        this.open(options);
      }
    };

    document.addEventListener('mouseleave', onPointerLeave, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    this.removeListeners.push(() => {
      document.removeEventListener('mouseleave', onPointerLeave);
      window.removeEventListener('scroll', onScroll);
    });
  }

  private hasRecentlyOpened(storageKey: string, days: number): boolean {
    if (days <= 0) return false;

    try {
      const value = window.localStorage.getItem(storageKey);
      if (!value) return false;

      const lastOpenedAt = Number(value);
      if (!Number.isFinite(lastOpenedAt)) return false;

      return Date.now() - lastOpenedAt < days * 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  }
}
